import React, { useEffect, useState, useContext } from "react";
import { Switch, Route, withRouter, Redirect } from "react-router-dom";
import Zendesk from "react-zendesk";

import { UserContext, USER_ROLES } from "./components/UserProvider";
import TopNav, { TOP_NAV_VARIANTS } from "./components/TopNav";
import SideNav from "./components/SideNav";
import ErrorPage from "./components/ErrorPage";
import { fetchCompanyProfile } from "./api/xchange-api";
import GeneralMeetingRoom from "./components/GeneralMeetingRoom";
import ReceptionMeetingRoom from "./components/ReceptionMeetingRoom";
import Home from "./components/Home";
import useScript from "./hooks/useScript";
import { BASE_URL, XCHANGE_PLATFORM_NAME, APP_EVENT_ID } from "./utils/consts";

import "bootstrap/dist/css/bootstrap.css";
import "bootstrap/dist/js/bootstrap.bundle";
import "font-awesome/css/font-awesome.min.css";

import "@syncfusion/ej2-base/styles/bootstrap.css";
import "@syncfusion/ej2-buttons/styles/bootstrap.css";
import "@syncfusion/ej2-calendars/styles/bootstrap.css";
import "@syncfusion/ej2-dropdowns/styles/bootstrap.css";
import "@syncfusion/ej2-inputs/styles/bootstrap.css";
import "@syncfusion/ej2-splitbuttons/styles/bootstrap.css";
import "@syncfusion/ej2-lists/styles/bootstrap.css";
import "@syncfusion/ej2-navigations/styles/bootstrap.css";
import "@syncfusion/ej2-popups/styles/bootstrap.css";
import "@syncfusion/ej2-schedule/styles/bootstrap.css";

import "./App.css";
import LoadingScreen from "./components/LoadingScreen";

function App({ location }) {
  const {
    userProfile,
    fullName,
    role,
    companyId,
    meetingId,
    isInReceptionRoom,
  } = useContext(UserContext);
  const [collapsed, setCollapsed] = useState(true);
  const [loadingCompanyProfile, setLoadingCompanyProfile] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(undefined);
  const [hosts, setHosts] = useState([]);
  const [loaded, error] = useScript(
    `${process.env.REACT_APP_XCHANGE_BASE_URL}/Scripts/Xchange/MessagingHub.js`
  );
  useScript(
    `${process.env.REACT_APP_XCHANGE_BASE_URL}/Scripts/Xchange/main.js`
  );
  const [chatInitialized, setChatInitialized] = useState(false);
  const ZENDESK_KEY = process.env.REACT_APP_ZENDESK_KEY;

  // Load Company Profile
  useEffect(() => {
    const getCompanyProfile = async (id) => {
      setLoadingCompanyProfile(true);
      const profile = await fetchCompanyProfile(id, userProfile.AttendeeID);
      setLoadingCompanyProfile(false);
      if (profile && profile.length) {
        const allHostsFlattened = profile.reduce((allHosts, companyProfile) => {
          return [...allHosts, ...companyProfile.users];
        }, []);

        const company = profile.filter((company) => {
          return company.CompanyProfile.CompanyProfileID === +companyId;
        });

        if (company) {
          setCompanyProfile(company[0].CompanyProfile);
        } else {
          console.error(
            `Error loading company profile for company id ${companyId}`
          );
        }

        setHosts(allHostsFlattened);
      } else {
        setCompanyProfile(null);
      }
    };

    if (companyId && userProfile) {
      getCompanyProfile(companyId);
    }
  }, [companyId, userProfile]);

  useEffect(() => {
    if (loaded && !error) {
      let chat = window.MessagingHub;
      if (chat) {
        setChatInitialized(true);
        window.messagingHubURL = `${BASE_URL}/${XCHANGE_PLATFORM_NAME}/${APP_EVENT_ID}/messaginghub/`;
        chat.initialize(`${APP_EVENT_ID}`);
      }
    }
  }, [loaded, error]);

  // The following code will redirect users (based on their role) to the correct
  // meeting/reception room.
  // Example: RECEPTIONIST will always be redirected to the reception room, a DELEGATE
  // will be redirected to the reception room
  if (
    role === USER_ROLES.DELEGATE &&
    !isInReceptionRoom &&
    (!location.state || !location.state.redirectToMeeting)
  ) {
    return (
      <Redirect to={`/${companyId}/reception/${meetingId ? meetingId : ""}`} />
    );
  }

  if (role === USER_ROLES.RECEPTIONIST && !isInReceptionRoom) {
    return <Redirect to={`/${companyId}/reception`} />;
  }

  if (role === USER_ROLES.HOST && isInReceptionRoom && meetingId) {
    return <Redirect to={`/${companyId}/meeting/${meetingId}`} />;
  }

  if (
    (role === USER_ROLES.HOST || role === USER_ROLES.ADMIN) &&
    isInReceptionRoom &&
    !meetingId
  ) {
    return <Redirect to={`/${companyId}/home`} />;
  }

  if (companyProfile === null) {
    return <Redirect to="/error" />;
  }

  return companyId && loadingCompanyProfile ? (
    <div className="d-flex flex-column justify-content-center align-items-center container-fluid min-vh-100">
      <LoadingScreen />
    </div>
  ) : (
    <>
      <div className={`App ${collapsed && "sidebar-collapsed"}`}>
        <div className="container-fluid d-flex min-vh-100 flex-column">
          <div className="row sticky-top">
            <TopNav
              variant={TOP_NAV_VARIANTS.large}
              username={fullName}
              userProfile={userProfile}
            />
            <TopNav
              variant={TOP_NAV_VARIANTS.small}
              username={fullName}
              userProfile={userProfile}
            />
          </div>
          <div className="row flex-fill d-flex flex-nowrap">
            <SideNav
              collapsed={collapsed}
              toggleCollapse={setCollapsed}
              userProfile={userProfile}
              companyId={companyId}
            />
            <div className="mainContentContainer w-100">
              <div className="container-fluid">
                <div className="row h-100">
                  <main role="main" className="col-sm-12">
                    <Switch>
                      <Route path="/:companyId/home">
                        <Home
                          sidebarCollapsed={collapsed}
                          companyProfile={companyProfile}
                          hosts={hosts}
                          chatLoaded={chatInitialized}
                        />
                      </Route>
                      <Route path="/:companyId/meeting/:meetingId">
                        <GeneralMeetingRoom
                          sidebarCollapsed={collapsed}
                          companyProfile={companyProfile}
                          hosts={hosts}
                          meetingId={meetingId}
                          chatLoaded={chatInitialized}
                        />
                      </Route>
                      <Route path="/:companyId/reception/:meetingId">
                        <ReceptionMeetingRoom
                          sidebarCollapsed={collapsed}
                          companyProfile={companyProfile}
                          hosts={hosts}
                          meetingId={meetingId}
                          chatLoaded={chatInitialized}
                        />
                      </Route>
                      <Route path="/:companyId/reception">
                        <ReceptionMeetingRoom
                          sidebarCollapsed={collapsed}
                          companyProfile={companyProfile}
                          hosts={hosts}
                          chatLoaded={chatInitialized}
                        />
                      </Route>
                      <Route path="/error">
                        <ErrorPage errorText="Invalid parameters" />
                      </Route>
                      <Route path="*">
                        <ErrorPage errorText="Invalid path" />
                      </Route>
                    </Switch>
                  </main>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Zendesk zendeskKey={ZENDESK_KEY} />
    </>
  );
}

export default withRouter(App);
